import Session from "@lib/session";

/**
 * BE's current service area as a string for the `X-Service-Area` traffic header
 * (traffic-telemetry T9 / D13), or undefined when none is set.
 *
 * The area is the booking's resolved area if the rider has picked pickup/dropoff,
 * ELSE the site's DEFAULT region held in the session config. The default is how the
 * service area is chosen for almost all BE traffic — `DefaultServiceArea` (and the CMS
 * `FrameConfig`) write it into `config.serviceArea`, while `booking.serviceArea` is only
 * set once locations resolve — so reading the booking alone would leave nearly every
 * request in the counter's "no area yet" bucket. Threaded through the shared Apollo
 * client as a PER-REQUEST getter so napi's traffic counter can pivot BE traffic by area;
 * the area is volatile per-tab session state, so it is read fresh on each request rather
 * than captured when the client is built. A falsy area (unset, or the falsy `0` BE itself
 * treats as "no area" — see UpfrontCollection) yields undefined, so the header is omitted
 * and the request lands in the counter's labeled "no area yet" bucket.
 *
 * PASSIVE reads (`touchExpiration: false`): this runs on EVERY browser nAPI request, so it
 * must not touch the booking session's rolling expiry — the default `Session.getBooking()`
 * / `Session.getConfig()` reads would call `touchExpiration()` (and, when the session is
 * already expired, `clearSession()`) every time, coupling a telemetry read to session
 * lifecycle and, given background/retry traffic, effectively preventing idle timeout.
 * Reading the raw stored booking/config with `touchExpiration: false` keeps the getter a
 * pure observation.
 *
 * CRASH-SAFE: a telemetry read must NEVER fail a request. `Session.get` touches
 * sessionStorage, whose access THROWS in privacy mode / sandboxed iframes (be2.0's
 * `Session.get` doesn't guard the raw `getItem`), and this getter runs inside the Apollo
 * authLink on every browser request — an uncaught throw would reject the whole GraphQL
 * operation. Any throw is swallowed to undefined (→ header omitted → "no area yet").
 *
 * A stable module-level reference on purpose: ApolloProvider memoizes the Apollo client on
 * this getter's identity, so a fresh closure each render would needlessly recreate it.
 */
export const getServiceAreaId = (): string | undefined => {
  try {
    // Booking's resolved area first (the most specific), else the site's default region.
    const area =
      Session.get("booking", false)?.serviceArea || Session.get("config", false)?.serviceArea;
    // Emit only what the sensor accepts (a 1–6 digit id): a positive integer ≤ 999999.
    // A decimal or 7+-digit value would stringify to a header the sensor rejects
    // (silently → "no area yet"), so reject it here rather than send a dead header.
    return typeof area === "number" && Number.isInteger(area) && area > 0 && area <= 999999
      ? String(area)
      : undefined;
  } catch {
    return undefined;
  }
};
